import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
	CheckboxField,
	FormControl, FormField, FormItem, FormLabel, FormMessage,
	NumberInput, RadioGroup,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { FieldHint } from '@/components/field-hint'
import { SectionHeading } from './section-heading'
import { CheckboxOption, RadioOption } from './form-options'
import {
	COWORKING_EXTRA_VALUES,
	COWORKING_SERVICE_VALUES,
	EQUIPMENT_VALUES,
	FOOD_AND_DRINK_VALUES,
	KEY_AMENITY_VALUES,
	LAND_CATEGORY_VALUES,
	RESIDENTIAL_PARKING_VALUES,
	ROOM_ADJACENCY_VALUES,
	WALLS_TYPE_VALUES,
} from './enum-options'
import type { AdditionalCategory } from '@rentflowdev/api'

const WORKPLACE_STATUS_OPTIONS = ['FIXED', 'FLOATING', 'PRIVATE_OFFICE'] as const
const AMENITIES_INCLUSION_OPTIONS = ['INCLUDED', 'NOT_INCLUDED', 'PARTIALLY_INCLUDED'] as const

/**
 * The five amenity sets. The values themselves live in `./enum-options`, which
 * the schema validates against too — kept in one place because a list written
 * out here and again in the schema is a pair that can disagree.
 */
const COWORKING_CHECKBOX_GROUPS = [
	{ field: 'keyAmenities', labelKey: 'keyAmenities', optionKey: 'keyAmenityOptions',
	  values: KEY_AMENITY_VALUES },
	{ field: 'equipment', labelKey: 'equipment', optionKey: 'equipmentOptions',
	  values: EQUIPMENT_VALUES },
	{ field: 'foodAndDrinks', labelKey: 'foodAndDrinks', optionKey: 'foodAndDrinkOptions',
	  values: FOOD_AND_DRINK_VALUES },
	{ field: 'coworkingServices', labelKey: 'coworkingServices', optionKey: 'coworkingServiceOptions',
	  values: COWORKING_SERVICE_VALUES },
	{ field: 'coworkingExtras', labelKey: 'coworkingExtras', optionKey: 'coworkingExtraOptions',
	  values: COWORKING_EXTRA_VALUES },
] as const

const GARAGE_KIND_OPTIONS = ['GARAGE', 'PARKING_SPACE'] as const
const GARAGE_TYPE_OPTIONS = ['REINFORCED_CONCRETE', 'BRICK', 'METAL'] as const
const PARKING_SPACE_TYPE_OPTIONS = ['MULTILEVEL', 'UNDERGROUND', 'COVERED', 'OPEN'] as const
const ENTRANCE_OPTIONS = ['STREET', 'YARD'] as const
const COMMERCIAL_HEATING_OPTIONS = ['NONE', 'CENTRAL', 'AUTONOMOUS'] as const
const RETAIL_PLACEMENT_OPTIONS = ['STREET_RETAIL', 'IN_SHOPPING_CENTER'] as const

// The subsets differ per type, but every value comes from one enum — the
// whole list lives in ./enum-options, which the schema validates against too.
const ADDITIONAL_CATEGORY_OPTIONS: Record<string, readonly AdditionalCategory[]> = {
	OFFICE: ['FREE_PURPOSE', 'RETAIL', 'COWORKING'],
	RETAIL: ['OFFICE', 'CATERING'],
	WAREHOUSE: ['FREE_PURPOSE', 'BUILDING', 'PRODUCTION', 'CAR_SERVICE', 'WAREHOUSE'],
	COWORKING: ['FREE_PURPOSE', 'OFFICE'],
}

const ONLINE_VIEWING_OPTIONS = [
	{ value: 'YES', labelKey: 'properties.details.onlineViewingOptions.YES' },
	{ value: 'NO',  labelKey: 'properties.details.onlineViewingOptions.NO' },
] as const

const OFFICE_LAYOUT_OPTIONS = ['CABINET', 'OPEN'] as const
const FINISHING_OPTIONS = ['NONE', 'CLEAN', 'OFFICE'] as const
const COMMERCIAL_BUILDING_TYPE_OPTIONS = [
	'BUSINESS_CENTER', 'SHOPPING_CENTER', 'ADMINISTRATIVE', 'RESIDENTIAL_BUILDING', 'OTHER',
] as const
const BUILDING_CLASS_OPTIONS = ['A', 'B', 'C'] as const
const COMMERCIAL_PARKING_OPTIONS = ['NONE', 'STREET', 'IN_BUILDING'] as const

const HOUSING_OPTIONS = [
	{ value: 'APARTMENT',  labelKey: 'properties.details.housingTypes.apartment' },
	{ value: 'APARTMENTS', labelKey: 'properties.details.housingTypes.apartments' },
] as const

const ROOM_LOCATION_OPTIONS = [
	{ value: 'APARTMENT', labelKey: 'properties.details.roomLocations.apartment' },
	{ value: 'HOSTEL',    labelKey: 'properties.details.roomLocations.hostel' },
	{ value: 'HOTEL',     labelKey: 'properties.details.roomLocations.hotel' },
] as const

const ROOM_HOUSING_TYPE_OPTIONS = [
	{ value: 'ROOM', labelKey: 'properties.details.roomHousingTypes.room' },
	{ value: 'BED',  labelKey: 'properties.details.roomHousingTypes.bed' },
] as const

const BATHROOM_OPTIONS = [
	{ value: 'COMBINED', labelKey: 'properties.details.bathroomTypes.combined' },
	{ value: 'SEPARATE', labelKey: 'properties.details.bathroomTypes.separate' },
] as const

const HOUSE_KIND_OPTIONS = [
	{ value: 'HOUSE',     labelKey: 'properties.details.houseKinds.house' },
	{ value: 'DACHA',     labelKey: 'properties.details.houseKinds.dacha' },
	{ value: 'COTTAGE',   labelKey: 'properties.details.houseKinds.cottage' },
	{ value: 'TOWNHOUSE', labelKey: 'properties.details.houseKinds.townhouse' },
] as const

const BATHROOM_LOCATION_OPTIONS = [
	{ value: 'INSIDE',  labelKey: 'properties.details.bathroomLocations.inside' },
	{ value: 'OUTSIDE', labelKey: 'properties.details.bathroomLocations.outside' },
] as const

const COMMUNICATIONS_OPTIONS = [
	{ value: 'electricity', labelKey: 'properties.details.communicationsItems.electricity' },
	{ value: 'gas',         labelKey: 'properties.details.communicationsItems.gas' },
	{ value: 'heating',     labelKey: 'properties.details.communicationsItems.heating' },
	{ value: 'sewerage',    labelKey: 'properties.details.communicationsItems.sewerage' },
] as const

const TRANSPORT_ACCESS_OPTIONS = [
	{ value: 'road',      labelKey: 'properties.details.transportAccessItems.road' },
	{ value: 'bus_stop',  labelKey: 'properties.details.transportAccessItems.busStop' },
	{ value: 'railway',   labelKey: 'properties.details.transportAccessItems.railway' },
] as const

const NEARBY_INFRASTRUCTURE_OPTIONS = [
	{ value: 'shop',    labelKey: 'properties.details.nearbyInfrastructureItems.shop' },
	{ value: 'pharmacy', labelKey: 'properties.details.nearbyInfrastructureItems.pharmacy' },
	{ value: 'kindergarten', labelKey: 'properties.details.nearbyInfrastructureItems.kindergarten' },
	{ value: 'school',  labelKey: 'properties.details.nearbyInfrastructureItems.school' },
] as const

const RENOVATION_OPTIONS = [
	{ value: 'NONE',     labelKey: 'properties.details.renovationTypes.none' },
	{ value: 'COSMETIC', labelKey: 'properties.details.renovationTypes.cosmetic' },
	{ value: 'EURO',     labelKey: 'properties.details.renovationTypes.euro' },
	{ value: 'DESIGNER', labelKey: 'properties.details.renovationTypes.designer' },
] as const

const BUILDING_TYPE_OPTIONS = [
	{ value: 'BRICK',          labelKey: 'properties.details.buildingTypes.brick' },
	{ value: 'PANEL',          labelKey: 'properties.details.buildingTypes.panel' },
	{ value: 'BLOCK',          labelKey: 'properties.details.buildingTypes.block' },
	{ value: 'MONOLITH',       labelKey: 'properties.details.buildingTypes.monolith' },
	{ value: 'MONOLITH_BRICK', labelKey: 'properties.details.buildingTypes.monolithBrick' },
	{ value: 'WOOD',           labelKey: 'properties.details.buildingTypes.wood' },
] as const

const ELEVATOR_OPTIONS = [
	{ value: 'NONE', labelKey: 'common.no' },
	{ value: '1',    labelKey: '' },
	{ value: '2',    labelKey: '' },
	{ value: '3',    labelKey: '' },
	{ value: '4',    labelKey: '' },
] as const

const FURNITURE_OPTIONS = [
	{ value: 'kitchen',  labelKey: 'properties.details.furnitureItems.kitchen' },
	{ value: 'wardrobe', labelKey: 'properties.details.furnitureItems.wardrobe' },
	{ value: 'beds',     labelKey: 'properties.details.furnitureItems.beds' },
] as const

const APPLIANCES_COL_1 = [
	{ value: 'ac',        labelKey: 'properties.details.applianceItems.ac' },
	{ value: 'fridge',    labelKey: 'properties.details.applianceItems.fridge' },
	{ value: 'stove',     labelKey: 'properties.details.applianceItems.stove' },
	{ value: 'microwave', labelKey: 'properties.details.applianceItems.microwave' },
] as const

const APPLIANCES_COL_2 = [
	{ value: 'washer',     labelKey: 'properties.details.applianceItems.washer' },
	{ value: 'dishwasher', labelKey: 'properties.details.applianceItems.dishwasher' },
	{ value: 'waterheat',  labelKey: 'properties.details.applianceItems.waterheat' },
	{ value: 'tv',         labelKey: 'properties.details.applianceItems.tv' },
] as const

const INTERNET_TV_OPTIONS = [
	{ value: 'wifi',       labelKey: 'properties.details.internetItems.wifi' },
	{ value: 'television', labelKey: 'properties.details.internetItems.television' },
] as const

const YARD_OPTIONS = [
	{ value: 'closed_area',   labelKey: 'properties.details.yardItems.closedArea' },
	{ value: 'playground',    labelKey: 'properties.details.yardItems.playground' },
	{ value: 'sports_ground', labelKey: 'properties.details.yardItems.sportsGround' },
] as const

// Дом стоит на своём участке, поэтому и парковка у него своя: гараж или
// место, а не подземный паркинг многоквартирного дома.
const HOUSE_PARKING_OPTIONS = [
	{ value: 'garage',       labelKey: 'properties.details.parkingTypes.garage' },
	{ value: 'parking_spot', labelKey: 'properties.details.parkingTypes.parkingSpot' },
] as const

const PARKING_OPTIONS = [
	{ value: 'underground', labelKey: 'properties.details.parkingTypes.underground' },
	{ value: 'multi_level', labelKey: 'properties.details.parkingTypes.multiLevel' },
	{ value: 'open_yard',   labelKey: 'properties.details.parkingTypes.openYard' },
	{ value: 'gated_yard',  labelKey: 'properties.details.parkingTypes.gatedYard' },
	{ value: 'garage',       labelKey: 'properties.details.parkingTypes.garage' },
	{ value: 'parking_spot', labelKey: 'properties.details.parkingTypes.parkingSpot' },
] as const

const YES_NO_OPTIONS = [
	{ value: 'NO',  labelKey: 'common.no' },
	{ value: 'YES', labelKey: 'common.yes' },
] as const

const MAX_RESIDENTS_OPTIONS = [
	{ value: '1', labelKey: 'properties.details.tenantCounts.1' },
	{ value: '2', labelKey: 'properties.details.tenantCounts.2' },
	{ value: '3', labelKey: 'properties.details.tenantCounts.3' },
	{ value: '4', labelKey: 'properties.details.tenantCounts.4' },
	{ value: '5', labelKey: 'properties.details.tenantCounts.5' },
] as const

export function Step2Details() {
	const form = useFormContext()
	const { control } = form
	const { t } = useTranslation()
	const tr = (key: string, fallback: string) => key ? t(key) : fallback

	const type: string = form.watch('type') ?? 'APARTMENT'
	const isApartment = type === 'APARTMENT' || type === 'ROOM'
	// A flat, and not a room inside one: the kitchen area and how the rooms
	// adjoin describe the whole dwelling, which a room listing is not.
	const isFlat      = type === 'APARTMENT'
	const isRoom      = type === 'ROOM'
	const isHouse     = type === 'HOUSE'
	const isOffice    = type === 'OFFICE'
	const isRetail    = type === 'RETAIL'
	// Торговое и складское берут коммерческий блок офиса и добавляют свои три
	// вопроса; планировка и онлайн-показ — офисные, их они не спрашивают.
	const isCommercial = isOffice || isRetail || type === 'WAREHOUSE' || type === 'COWORKING'
	const categoryOptions = ADDITIONAL_CATEGORY_OPTIONS[type] ?? []
	// A garage has no bathroom, no renovation, no furniture and no house rules:
	// nobody lives in it. Excluded from those blocks rather than shown them
	// empty — the same reason an office no longer gets «О доме».
	const isCoworking = type === 'COWORKING'
	const workplaceStatus = form.watch('workplaceStatus')
	const isGarage = type === 'GARAGE'
	const garageKind = form.watch('garageKind')
	const commercialParking = form.watch('commercialParking')
	const isLand      = type === 'LAND'
	const roomLocation: string | undefined = form.watch('roomLocation')
	const roomHousingType: string | undefined = form.watch('roomHousingType')

	return (
		<div className="flex flex-col gap-5 px-4 pt-2 pb-8">
			<section className="flex flex-col gap-5">
				<SectionHeading>{t(`properties.card.aboutType.${type}`, t('properties.details.aboutApartment'))}</SectionHeading>
				{type === 'APARTMENT' && (
					<FormField
						control={control}
						name="housingType"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('properties.details.housingType')}</FormLabel>
								<FormControl>
									<RadioGroup
										value={field.value}
										onValueChange={field.onChange}
										name={field.name}
										className="flex flex-wrap gap-2"
									>
										{HOUSING_OPTIONS.map((opt) => (
											<RadioOption key={opt.value} value={opt.value} id={`housing-${opt.value}`} label={t(opt.labelKey)} />
										))}
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
				{isRoom && (
					<>
						<FormField
							control={control}
							name="roomLocation"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('properties.details.roomLocation')}</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={(v) => {
												field.onChange(v)
												// roomsInApartment is only rendered for APARTMENT — clear it
												// so a hidden stale value is not submitted.
												if (v !== 'APARTMENT') form.setValue('roomsInApartment', undefined)
											}}
											name={field.name}
											className="flex flex-wrap gap-2"
										>
											{ROOM_LOCATION_OPTIONS.map((opt) => (
												<RadioOption key={opt.value} value={opt.value} id={`room-location-${opt.value}`} label={t(opt.labelKey)} />
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{roomLocation === 'APARTMENT' && (
							<NumberInput name="roomsInApartment" label={t('properties.details.roomsInApartment')} placeholder="3" min={1} className="w-44" />
						)}
						<FormField
							control={control}
							name="roomHousingType"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('properties.details.roomHousingType')}</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={(v) => {
												field.onChange(v)
												// bedCount is only rendered for BED — clear it so a hidden
												// stale value is not submitted.
												if (v !== 'BED') form.setValue('bedCount', undefined)
											}}
											name={field.name}
											className="flex flex-wrap gap-2"
										>
											{ROOM_HOUSING_TYPE_OPTIONS.map((opt) => (
												<RadioOption key={opt.value} value={opt.value} id={`room-housing-${opt.value}`} label={t(opt.labelKey)} />
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{roomHousingType === 'BED' && (
							<NumberInput name="bedCount" label={t('properties.details.bedCount')} placeholder="1" min={1} className="w-44" />
						)}
						<NumberInput name="sleepingPlaces" label={t('properties.details.sleepingPlaces')} placeholder="1" min={1} className="w-44" />
						<div className="flex flex-col gap-4">
							<CheckboxField name="eventsAllowed" label={t('properties.details.eventsAllowed')} labelClassName="font-[500] cursor-pointer" />
							<CheckboxField name="fireplace"     label={t('properties.details.fireplace')}     labelClassName="font-[500] cursor-pointer" />
						</div>
					</>
				)}
				{isHouse && (
					<>
						<FormField
							control={control}
							name="houseKind"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('properties.details.houseKind')}</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={field.onChange}
											name={field.name}
											className="flex flex-wrap gap-2"
										>
											{HOUSE_KIND_OPTIONS.map((opt) => (
												<RadioOption key={opt.value} value={opt.value} id={`house-kind-${opt.value}`} label={t(opt.labelKey)} />
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{/* What the walls are made of, not the kind of building: a block of
						    flats answers the same question through `buildingType`, whose
						    list names neither timber nor a sandwich panel. */}
						<FormField
							control={control}
							name="wallsType"
							render={({ field }) => (
								<FormItem className="w-1/2">
									<FormLabel>{t('properties.details.wallsType')}</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder={t('properties.details.notSelected')} />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{WALLS_TYPE_VALUES.map((value) => (
												<SelectItem key={value} value={value}>
													{t(`properties.details.wallsTypeOptions.${value}`)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						{/* Сотки, а у участка то же поле — метры. Подпись без единицы
						    была одна на оба, и введённое число уезжало в Авито
						    отличаясь в сто раз. */}
						<NumberInput name="plotArea" label={t('properties.details.plotAreaSotki')} placeholder="6" min={1} className="w-44" />
						<div className="flex flex-col gap-4">
							<CheckboxField name="sauna"   label={t('properties.details.sauna')}   labelClassName="font-[500] cursor-pointer" />
							<CheckboxField name="pool"    label={t('properties.details.pool')}    labelClassName="font-[500] cursor-pointer" />
							<CheckboxField name="terrace" label={t('properties.details.terrace')} labelClassName="font-[500] cursor-pointer" />
						</div>
						{/* Checkboxes, not a choice of one: a dacha commonly has the
						    lavatory outside and the shower indoors, and Avito lets both
						    be ticked. A radio group could only ever record half of it. */}
						<FormField
							control={control}
							name="bathroomLocations"
							render={({ field }) => {
								const checked: string[] = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.bathroomLocation')}</FormLabel>
										<div className="flex flex-wrap gap-4">
											{BATHROOM_LOCATION_OPTIONS.map((opt) => (
												<CheckboxOption
													key={opt.value}
													id={`bathroom-location-${opt.value}`}
													label={t(opt.labelKey)}
													checked={checked.includes(opt.value)}
													onCheckedChange={(next) =>
														field.onChange(next ? [...checked, opt.value] : checked.filter((v) => v !== opt.value))
													}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
						<FormField
							control={control}
							name="communications"
							render={({ field }) => {
								const checked = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.communications')}</FormLabel>
										<div className="flex flex-col gap-4">
											{COMMUNICATIONS_OPTIONS.map((item) => (
												<CheckboxOption
													key={item.value}
													id={`communications-${item.value}`}
													label={t(item.labelKey)}
													checked={checked.includes(item.value)}
													onCheckedChange={(next) =>
														field.onChange(next ? [...checked, item.value] : checked.filter((v: string) => v !== item.value))
													}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
						<FormField
							control={control}
							name="transportAccess"
							render={({ field }) => {
								const checked = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.transportAccess')}</FormLabel>
										<div className="flex flex-col gap-4">
											{TRANSPORT_ACCESS_OPTIONS.map((item) => (
												<CheckboxOption
													key={item.value}
													id={`transport-access-${item.value}`}
													label={t(item.labelKey)}
													checked={checked.includes(item.value)}
													onCheckedChange={(next) =>
														field.onChange(next ? [...checked, item.value] : checked.filter((v: string) => v !== item.value))
													}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
						<FormField
							control={control}
							name="nearbyInfrastructure"
							render={({ field }) => {
								const checked = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.nearbyInfrastructure')}</FormLabel>
										<div className="flex flex-col gap-4">
											{NEARBY_INFRASTRUCTURE_OPTIONS.map((item) => (
												<CheckboxOption
													key={item.value}
													id={`nearby-infrastructure-${item.value}`}
													label={t(item.labelKey)}
													checked={checked.includes(item.value)}
													onCheckedChange={(next) =>
														field.onChange(next ? [...checked, item.value] : checked.filter((v: string) => v !== item.value))
													}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
					</>
				)}
				{/* A plot of land: what it may legally be used for. It decides which
				    sections the ad may appear in at all, so it is asked as a field
				    rather than left to the description. */}
				{isLand && (
					<FormField
						control={control}
						name="landCategory"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('properties.details.landCategory')}</FormLabel>
								<FormControl>
									<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
										{LAND_CATEGORY_VALUES.map((value) => (
											<RadioOption key={value} value={value} id={`land-category-${value}`} label={t(`properties.details.landCategoryOptions.${value}`)} />
										))}
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
				{/* Commercial premises. Replaces the residential «О доме» section
				    rather than joining it: an office is not asked about lifts or a
				    rubbish chute, and a flat is not asked about its building class.
				    Retail, warehouse and coworking share this block as they land. */}
				{/* Garage and parking space. One type with two question sets — the
				    lists of kinds do not overlap, so only the one that applies is
				    shown. Everything else about them is the shared address, area,
				    photos and terms. */}
				{isGarage && (
					<>
						<FormField
							control={control}
							name="garageKind"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('properties.details.garageKind')}</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={(v) => {
												field.onChange(v)
												// The other list's answer is dropped: a garage is
												// not made of "открытая стоянка", and leaving it
												// behind would submit a kind of the wrong thing.
												form.setValue(v === 'GARAGE' ? 'parkingSpaceType' : 'garageType', undefined)
											}}
											name={field.name}
											className="flex flex-wrap gap-2"
										>
											{GARAGE_KIND_OPTIONS.map((value) => (
												<RadioOption key={value} value={value} id={`garage-kind-${value}`} label={t(`properties.details.garageKindOptions.${value}`)} />
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{garageKind === 'GARAGE' && (
							<FormField
								control={control}
								name="garageType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('properties.details.garageType')}</FormLabel>
										<FormControl>
											<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
												{GARAGE_TYPE_OPTIONS.map((value) => (
													<RadioOption key={value} value={value} id={`garage-type-${value}`} label={t(`properties.details.garageTypeOptions.${value}`)} />
												))}
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}
						{garageKind === 'PARKING_SPACE' && (
							<FormField
								control={control}
								name="parkingSpaceType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('properties.details.parkingSpaceType')}</FormLabel>
										<FormControl>
											<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
												{PARKING_SPACE_TYPE_OPTIONS.map((value) => (
													<RadioOption key={value} value={value} id={`parking-space-type-${value}`} label={t(`properties.details.parkingSpaceTypeOptions.${value}`)} />
												))}
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}
						{/* Да/Нет, а не галочка: снятая галочка не отличает «охраны
						    нет» от «не ответили», а Авито спрашивает это выбором и
						    ждёт один из двух ответов. */}
						<FormField
							control={control}
							name="security"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('properties.details.security')}</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value === undefined ? undefined : field.value ? 'YES' : 'NO'}
											onValueChange={(v) => field.onChange(v === 'YES')}
											name={field.name}
											className="flex flex-wrap gap-2"
										>
											{YES_NO_OPTIONS.map((opt) => (
												<RadioOption key={opt.value} value={opt.value} id={`security-${opt.value}`} label={t(opt.labelKey)} />
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</>
				)}
				{isCommercial && (
					<>
						{/* «Проведу / Не хочу», а не галочка: снятая галочка молчит, а
						    Авито ждёт один из двух ответов и без него объявление
						    уйдёт как «показ не провожу». */}
						<FormField
							control={control}
							name="onlineViewing"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('properties.details.onlineViewing')}</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value === undefined ? undefined : field.value ? 'YES' : 'NO'}
											onValueChange={(v) => field.onChange(v === 'YES')}
											name={field.name}
											className="flex flex-wrap gap-2"
										>
											{ONLINE_VIEWING_OPTIONS.map((opt) => (
												<RadioOption key={opt.value} value={opt.value} id={`online-viewing-${opt.value}`} label={t(opt.labelKey)} />
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={control}
							name="officeLayouts"
							render={({ field }) => {
								const checked: string[] = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.officeLayouts')}</FormLabel>
										<div className="flex flex-wrap gap-4">
											{OFFICE_LAYOUT_OPTIONS.map((value) => (
												// The hint sits beside the option rather than under
												// the group: «Открытая» is the word that needs
												// explaining, not «Планировка».
												<div key={value} className="flex items-center gap-1.5">
													<CheckboxOption
														id={`office-layout-${value}`}
														label={t(`properties.details.officeLayoutOptions.${value}`)}
														checked={checked.includes(value)}
														onCheckedChange={(next) =>
															field.onChange(next ? [...checked, value] : checked.filter((v) => v !== value))
														}
													/>
													<FieldHint text={t(`properties.details.officeLayoutHints.${value}`)} />
												</div>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
						<div className="flex gap-2 items-start">
							<NumberInput name="ceilingHeight" label={t('properties.details.ceilingHeight')} placeholder="3.2" min={0} className="flex-1" />
							<NumberInput name="powerCapacity" label={t('properties.details.powerCapacity')} placeholder="15" min={0} className="flex-1" />
						</div>
						{/* Прямо под мощностью: вопрос про неё же — можно ли нарастить
						    по заявке. Арендатору под производство это решает, подходит
						    помещение или нет. */}
						<div className="flex items-center gap-1.5">
							<CheckboxField name="powerCapacityExpandable" label={t('properties.details.powerCapacityExpandable')} labelClassName="font-[500] cursor-pointer" />
							<FieldHint text={t('properties.details.powerCapacityExpandableHint')} />
						</div>
						<FormField
							control={control}
							name="finishing"
							render={({ field }) => (
								<FormItem className="w-1/2">
									<FormLabel>{t('properties.details.finishing')}</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder={t('properties.details.notSelected')} />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{FINISHING_OPTIONS.map((value) => (
												<SelectItem key={value} value={value}>
													{t(`properties.details.finishingOptions.${value}`)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						{/* «Ещё категории» есть у всех коммерческих: офис можно заодно
						    показать как свободное назначение, торговое или коворкинг. */}
						<FormField
							control={control}
							name="additionalCategories"
							render={({ field }) => {
								const checked: string[] = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.additionalCategories')}</FormLabel>
										<div className="flex flex-wrap gap-4">
											{categoryOptions.map((value) => (
												<CheckboxOption
													key={value}
													id={`additional-category-${value}`}
													label={t(`properties.details.additionalCategoryOptions.${value}`)}
													checked={checked.includes(value)}
													onCheckedChange={(next) =>
														field.onChange(next ? [...checked, value] : checked.filter((v) => v !== value))
													}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
						{/* Торговому и складскому: как входят в помещение, отдельный ли
						    это вход и есть ли отопление. Офис их не спрашивает. */}
						{!isOffice && (
							<>
								{isRetail && (
									<FormField
										control={control}
										name="retailPlacement"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('properties.details.retailPlacement')}</FormLabel>
												<FormControl>
													<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
														{RETAIL_PLACEMENT_OPTIONS.map((value) => (
															<RadioOption key={value} value={value} id={`retail-placement-${value}`} label={t(`properties.details.retailPlacementOptions.${value}`)} />
														))}
													</RadioGroup>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}
								<FormField
									control={control}
									name="entrance"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('properties.details.entrance')}</FormLabel>
											<FormControl>
												<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
													{ENTRANCE_OPTIONS.map((value) => (
														<RadioOption key={value} value={value} id={`entrance-${value}`} label={t(`properties.details.entranceOptions.${value}`)} />
													))}
												</RadioGroup>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<CheckboxField name="separateEntrance" label={t('properties.details.separateEntrance')} labelClassName="font-[500] cursor-pointer" />
								<FormField
									control={control}
									name="commercialHeating"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('properties.details.commercialHeating')}</FormLabel>
											<FormControl>
												<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
													{COMMERCIAL_HEATING_OPTIONS.map((value) => (
														<RadioOption key={value} value={value} id={`commercial-heating-${value}`} label={t(`properties.details.commercialHeatingOptions.${value}`)} />
													))}
												</RadioGroup>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</>
						)}
						<div className="flex flex-col gap-4">
							{/* The floor field names the largest one, so occupying more
							    than one has to be stated separately. */}
							<div className="flex items-center gap-1.5">
								<CheckboxField name="multipleFloors" label={t('properties.details.multipleFloors')} labelClassName="font-[500] cursor-pointer" />
								<FieldHint text={t('properties.details.multipleFloorsHint')} />
							</div>
							<CheckboxField name="partialRent" label={t('properties.details.partialRent')} labelClassName="font-[500] cursor-pointer" />
						</div>
					</>
				)}
				<div className="flex gap-2 items-start">
					{!isHouse && !isLand && !isGarage && (
						<NumberInput name="floor" label={t('properties.details.floor')} placeholder="3" min={1} className="flex-1" />
					)}
					{/* Not for a room: a room listing is one room, and its apartment's
					    count is asked above as `roomsInApartment`. Both were rendered
					    together, two fields reading the same to whoever fills them.
					    `rooms` also builds the title, so a room with it filled was
					    saved as «3-к Комната». */}
					{!isCommercial && !isLand && !isRoom && !isGarage && (
						<NumberInput name="rooms" label={t('properties.details.roomCount')} placeholder="1" min={1} className="flex-1" />
					)}
				</div>
				{/* How the rooms relate to one another, not how many there are: two
				    adjacent rooms and two isolated ones are different lets. */}
				{isFlat && (
					<FormField
						control={control}
						name="roomType"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('properties.details.roomType')}</FormLabel>
								<FormControl>
									<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
										{ROOM_ADJACENCY_VALUES.map((value) => (
											<RadioOption key={value} value={value} id={`room-type-${value}`} label={t(`properties.details.roomTypeOptions.${value}`)} />
										))}
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
				{isApartment && (
					<div className="flex flex-col gap-4">
						<CheckboxField name="balcony"  label={t('properties.details.balcony')}  labelClassName="font-[500] cursor-pointer" />
						<CheckboxField name="loggia"   label={t('properties.details.loggia')}   labelClassName="font-[500] cursor-pointer" />
						<CheckboxField name="wardrobe" label={t('properties.details.wardrobe')} labelClassName="font-[500] cursor-pointer" />
					</div>
				)}
				<div className="flex gap-2 items-start">
					<NumberInput name="totalArea"  label={t(isLand ? 'properties.details.plotAreaSqm' : 'properties.details.totalArea')}  placeholder={isLand ? '600' : '78'} min={1} className="flex-1" />
					{!isCommercial && !isLand && !isGarage && (
						<NumberInput name="livingArea" label={t('properties.details.livingArea')} placeholder="56" min={1} className="flex-1" />
					)}
					{/* Separate from the living area, which excludes it: Avito asks
					    for the kitchen under a tag of its own. */}
					{isFlat && (
						<NumberInput name="kitchenSpace" label={t('properties.details.kitchenSpace')} placeholder="9" min={1} className="flex-1" />
					)}
				</div>
				{!isCommercial && !isLand && !isHouse && !isGarage && (
				<>
				<FormField
					control={control}
					name="bathroomType"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.bathroom')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={field.onChange}
									name={field.name}
									className="flex flex-col gap-4"
								>
									{BATHROOM_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`bathroom-${opt.value}`} label={t(opt.labelKey)} bordered={false} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				</>
				)}
				{!isLand && !isGarage && !isCommercial && (
				<FormField
					control={control}
					name="renovation"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.renovation')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={field.onChange}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{RENOVATION_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`renovation-${opt.value}`} label={t(opt.labelKey)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				)}
				{!isCommercial && !isLand && !isGarage && (
					<>
						<FormField
							control={control}
							name="furniture"
							render={({ field }) => {
								const checked = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.furniture')}</FormLabel>
										<div className="flex flex-col gap-4">
											{FURNITURE_OPTIONS.map((item) => (
												<CheckboxOption
													key={item.value}
													id={`furniture-${item.value}`}
													label={t(item.labelKey)}
													checked={checked.includes(item.value)}
													onCheckedChange={(next) =>
														field.onChange(next ? [...checked, item.value] : checked.filter((v: string) => v !== item.value))
													}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
						<FormField
							control={control}
							name="appliances"
							render={({ field }) => {
								const checked = field.value ?? []
								const toggle = (value: string, next: boolean) =>
									field.onChange(next ? [...checked, value] : checked.filter((v: string) => v !== value))
								return (
									<FormItem>
										<FormLabel>{t('properties.details.appliances')}</FormLabel>
										<div className="flex gap-16 items-start">
											<div className="flex flex-col gap-4">
												{APPLIANCES_COL_1.map((item) => (
													<CheckboxOption
														key={item.value}
														id={`appliances-${item.value}`}
														label={t(item.labelKey)}
														checked={checked.includes(item.value)}
														onCheckedChange={(next) => toggle(item.value, next)}
													/>
												))}
											</div>
											<div className="flex flex-col gap-4">
												{APPLIANCES_COL_2.map((item) => (
													<CheckboxOption
														key={item.value}
														id={`appliances-${item.value}`}
														label={t(item.labelKey)}
														checked={checked.includes(item.value)}
														onCheckedChange={(next) => toggle(item.value, next)}
													/>
												))}
											</div>
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
						<FormField
							control={control}
							name="internet"
							render={({ field }) => {
								const checked = field.value ?? []
								return (
									<FormItem>
										<FormLabel>{t('properties.details.internet')}</FormLabel>
										<div className="flex flex-col gap-4">
											{INTERNET_TV_OPTIONS.map((item) => (
												<CheckboxOption
													key={item.value}
													id={`internet-tv-${item.value}`}
													label={t(item.labelKey)}
													checked={checked.includes(item.value)}
													onCheckedChange={(next) =>
														field.onChange(next ? [...checked, item.value] : checked.filter((v: string) => v !== item.value))
													}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
					</>
				)}
			</section>

			{/* Not for an office. This block is a block of flats — материал стен,
			    консьерж, мусоропровод, лифты, детская площадка во дворе — and an
			    office was being shown all of it. Its own building questions are
			    different ones: назначение здания, класс, парковка. */}
			{/* The commercial counterpart of «О доме». Different questions, not a
			    subset: what the building is for and how it is classed, rather than
			    what it is built of and whether it has a concierge. */}
			{isCommercial && <section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.details.commercialBuilding')}</SectionHeading>
				<FormField
					control={control}
					name="commercialBuildingType"
					render={({ field }) => (
						<FormItem className="w-1/2">
							<FormLabel>{t('properties.details.commercialBuildingType')}</FormLabel>
							<Select value={field.value} onValueChange={field.onChange}>
								<FormControl>
									<SelectTrigger className="w-full">
										<SelectValue placeholder={t('properties.details.notSelected')} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{COMMERCIAL_BUILDING_TYPE_OPTIONS.map((value) => (
										<SelectItem key={value} value={value}>
											{t(`properties.details.commercialBuildingTypeOptions.${value}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={control}
					name="buildingClass"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.buildingClass')}</FormLabel>
							<FormControl>
								<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
									{BUILDING_CLASS_OPTIONS.map((value) => (
										<RadioOption key={value} value={value} id={`building-class-${value}`} label={value} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={control}
					name="commercialParking"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.commercialParking')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={(v) => {
										field.onChange(v)
										// Free, lorry-friendly and a count only mean something
										// where there is parking — kept from being submitted
										// as leftovers of an earlier answer.
										if (v === 'NONE') {
											form.setValue('parkingFree', undefined)
											form.setValue('parkingForTrucks', undefined)
											form.setValue('parkingSpaces', undefined)
										}
									}}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{COMMERCIAL_PARKING_OPTIONS.map((value) => (
										<RadioOption
											key={value}
											value={value}
											id={`commercial-parking-${value}`}
											label={t(`properties.details.commercialParkingOptions.${value}`)}
										/>
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				{commercialParking && commercialParking !== 'NONE' && (
					<>
						<div className="flex flex-col gap-4">
							<CheckboxField name="parkingFree" label={t('properties.details.parkingFree')} labelClassName="font-[500] cursor-pointer" />
							<CheckboxField name="parkingForTrucks" label={t('properties.details.parkingForTrucks')} labelClassName="font-[500] cursor-pointer" />
						</div>
						<NumberInput name="parkingSpaces" label={t('properties.details.parkingSpaces')} placeholder="12" min={0} className="w-44" />
					</>
				)}
			</section>}
			{/* Coworking. Everything above about the premises still applies — it
			    is a commercial let — and these are the questions that only make
			    sense once the premises are sold by the seat. */}
			{isCoworking && <section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.details.coworking')}</SectionHeading>
				<div className="flex gap-2 items-start">
					<NumberInput name="seatsCount" label={t('properties.details.seatsCount')} placeholder="40" min={0} className="flex-1" />
				</div>
				<div className="flex flex-col gap-4">
					<CheckboxField name="weekendsOpen" label={t('properties.details.weekendsOpen')} labelClassName="font-[500] cursor-pointer" />
					<CheckboxField name="aroundTheClock" label={t('properties.details.aroundTheClock')} labelClassName="font-[500] cursor-pointer" />
				</div>
				<FormField
					control={control}
					name="workplaceStatus"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.workplaceStatus')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={(v) => {
										field.onChange(v)
										// Area and desk count describe a private office. A hot
										// desk has neither, and keeping them would submit the
										// dimensions of a room that is not being let.
										if (v !== 'PRIVATE_OFFICE') {
											form.setValue('privateOfficeArea', undefined)
											form.setValue('privateOfficeSeats', undefined)
										}
									}}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{WORKPLACE_STATUS_OPTIONS.map((value) => (
										<RadioOption key={value} value={value} id={`workplace-status-${value}`} label={t(`properties.details.workplaceStatusOptions.${value}`)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				{workplaceStatus === 'PRIVATE_OFFICE' && (
					<div className="flex gap-2 items-start">
						<NumberInput name="privateOfficeArea" label={t('properties.details.privateOfficeArea')} placeholder="18" min={0} className="flex-1" />
						<NumberInput name="privateOfficeSeats" label={t('properties.details.privateOfficeSeats')} placeholder="4" min={0} className="flex-1" />
					</div>
				)}
				{/* Which amenities exist and whether they are paid for are two
				    questions, so they are two fields. */}
				<FormField
					control={control}
					name="amenitiesInclusion"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.amenitiesInclusion')}</FormLabel>
							<FormControl>
								<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
									{AMENITIES_INCLUSION_OPTIONS.map((value) => (
										<RadioOption key={value} value={value} id={`amenities-inclusion-${value}`} label={t(`properties.details.amenitiesInclusionOptions.${value}`)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				{COWORKING_CHECKBOX_GROUPS.map((group) => (
					<FormField
						key={group.field}
						control={control}
						name={group.field}
						render={({ field }) => {
							const checked: string[] = field.value ?? []
							return (
								<FormItem>
									<FormLabel>{t(`properties.details.${group.labelKey}`)}</FormLabel>
									<div className="flex flex-wrap gap-4">
										{group.values.map((value) => (
											<CheckboxOption
												key={value}
												id={`${group.field}-${value}`}
												label={t(`properties.details.${group.optionKey}.${value}`)}
												checked={checked.includes(value)}
												onCheckedChange={(next) =>
													field.onChange(next ? [...checked, value] : checked.filter((v) => v !== value))
												}
											/>
										))}
									</div>
									<FormMessage />
								</FormItem>
							)
						}}
					/>
				))}
			</section>}
			{!isLand && !isCommercial && !isGarage && <section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.details.aboutBuilding')}</SectionHeading>
				<FormField
					control={control}
					name="buildingType"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.buildingType')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={field.onChange}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{BUILDING_TYPE_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`building-${opt.value}`} label={t(opt.labelKey)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				{!isHouse && <div className="flex flex-col gap-4">
					<CheckboxField name="concierge" label={t('properties.details.concierge')} labelClassName="font-[500] cursor-pointer" />
					<CheckboxField name="chute"     label={t('properties.details.chute')}     labelClassName="font-[500] cursor-pointer" />
					<CheckboxField name="gas"       label={t('properties.details.gas')}       labelClassName="font-[500] cursor-pointer" />
				</div>}
				<div className="flex gap-2 items-start">
					<NumberInput name="yearBuilt"   label={t('properties.details.yearBuilt')}   placeholder="2012" min={1900} max={2100} className="flex-1" />
					<NumberInput name="totalFloors" label={t('properties.details.totalFloors')} placeholder="12"   min={1}    className="flex-1" />
				</div>
				{!isHouse && (
				<FormField
					control={control}
					name="passengerElevators"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.passengerElevator')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={field.onChange}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{ELEVATOR_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`pass-elev-${opt.value}`} label={tr(opt.labelKey, opt.value)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>)}
				{!isHouse && (
				<FormField
					control={control}
					name="freightElevators"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.freightElevator')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={field.onChange}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{ELEVATOR_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`freight-elev-${opt.value}`} label={tr(opt.labelKey, opt.value)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>)}
				{!isHouse && (
				<FormField
					control={control}
					name="yard"
					render={({ field }) => {
						const checked = field.value ?? []
						return (
							<FormItem>
								<FormLabel>{t('properties.details.yard')}</FormLabel>
								<div className="flex flex-col gap-4">
									{YARD_OPTIONS.map((item) => (
										<CheckboxOption
											key={item.value}
											id={`yard-${item.value}`}
											label={t(item.labelKey)}
											checked={checked.includes(item.value)}
											onCheckedChange={(next) =>
												field.onChange(next ? [...checked, item.value] : checked.filter((v: string) => v !== item.value))
											}
										/>
									))}
								</div>
								<FormMessage />
							</FormItem>
						)
					}}
				/>)}
				<FormField
					control={control}
					name="parking"
					render={({ field }) => {
						const checked = field.value ?? []
						return (
							<FormItem>
								<FormLabel>{t('properties.details.parking')}</FormLabel>
								<div className="flex flex-col gap-4">
									{(isHouse ? HOUSE_PARKING_OPTIONS : PARKING_OPTIONS).map((item) => (
										<CheckboxOption
											key={item.value}
											id={`parking-${item.value}`}
											label={t(item.labelKey)}
											checked={checked.includes(item.value)}
											onCheckedChange={(next) =>
												field.onChange(next ? [...checked, item.value] : checked.filter((v: string) => v !== item.value))
											}
										/>
									))}
								</div>
								<FormMessage />
							</FormItem>
						)
					}}
				/>
				{/* One answer, unlike the list above: that one names what the
				    building has, this one what parking the property itself comes
				    with. A room is not asked — it is let inside someone else's flat. */}
				{(isFlat || isHouse) && (
					<FormField
						control={control}
						name="parkingType"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('properties.details.parkingType')}</FormLabel>
								<FormControl>
									<RadioGroup value={field.value} onValueChange={field.onChange} name={field.name} className="flex flex-wrap gap-2">
										{RESIDENTIAL_PARKING_VALUES.map((value) => (
											<RadioOption key={value} value={value} id={`parking-type-${value}`} label={t(`properties.details.parkingTypeOptions.${value}`)} />
										))}
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
			</section>}

			{!isCommercial && !isLand && !isGarage && <section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.details.rules')}</SectionHeading>
				<FormField
					control={control}
					name="maxTenants"
					render={({ field }) => (
						<FormItem className="w-[21.125rem]">
							<FormLabel>{t('properties.details.maxTenants')}</FormLabel>
							<Select
								value={field.value !== undefined ? String(field.value) : undefined}
								onValueChange={(v) => field.onChange(Number(v))}
							>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder={t('properties.utilities.notSelected')} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{MAX_RESIDENTS_OPTIONS.map((opt) => (
										<SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={control}
					name="childrenAllowed"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.childrenAllowed')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value === undefined ? undefined : field.value ? 'YES' : 'NO'}
									onValueChange={(v) => field.onChange(v === 'YES')}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{YES_NO_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`children-${opt.value}`} label={t(opt.labelKey)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={control}
					name="petsAllowed"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.petsAllowed')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value === undefined ? undefined : field.value ? 'YES' : 'NO'}
									onValueChange={(v) => field.onChange(v === 'YES')}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{YES_NO_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`pets-${opt.value}`} label={t(opt.labelKey)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={control}
					name="smokingAllowed"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('properties.details.smokingAllowed')}</FormLabel>
							<FormControl>
								<RadioGroup
									value={field.value === undefined ? undefined : field.value ? 'YES' : 'NO'}
									onValueChange={(v) => field.onChange(v === 'YES')}
									name={field.name}
									className="flex flex-wrap gap-2"
								>
									{YES_NO_OPTIONS.map((opt) => (
										<RadioOption key={opt.value} value={opt.value} id={`smoking-${opt.value}`} label={t(opt.labelKey)} />
									))}
								</RadioGroup>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</section>}
		</div>
	)
}
